/**
 * Honeycomb Connections - BRONCANO
 * Sistema de partículas y conexiones tipo polen entre hexágonos
 */

class HoneycombConnections {
    constructor() {
        this.particles = [];
        this.connections = [];
        this.canvas = null;
        this.ctx = null;
        this.animationId = null;
        this.isActive = false;
        this.particleFrequency = 0.15;
        
        this.init();
    }
    
    init() {
        this.createCanvas();
        this.setupConnections();
        this.bindEvents();
    }
    
    createCanvas() {
        // Crear canvas para las partículas
        this.canvas = document.createElement('canvas');
        this.canvas.id = 'honeycomb-canvas';
        this.canvas.style.position = 'absolute';
        this.canvas.style.top = '0';
        this.canvas.style.left = '0';
        this.canvas.style.pointerEvents = 'none';
        this.canvas.style.zIndex = '5';
        
        this.ctx = this.canvas.getContext('2d');
        
        // Agregar al contenedor de la sección
        const waspSection = document.querySelector('.wasp-hex-section');
        if (waspSection) {
            waspSection.style.position = 'relative';
            waspSection.appendChild(this.canvas);
            this.resizeCanvas();
        }
    }
    
    resizeCanvas() {
        if (!this.canvas) return;
        
        const container = this.canvas.parentElement;
        if (container) {
            this.canvas.width = container.offsetWidth;
            this.canvas.height = container.offsetHeight;
        }
    }
    
    setupConnections() {
        // Definir conexiones entre elementos
        this.connections = [
            { from: '.wasp-hex-item:nth-child(1)', to: '.hex-group[data-ecosystem="satellite"]' },
            { from: '.wasp-hex-item:nth-child(2)', to: '.hex-group[data-ecosystem="tower"]' },
            { from: '.wasp-hex-item:nth-child(3)', to: '.hex-group[data-ecosystem="routes"]' },
            { from: '.wasp-hex-item:nth-child(4)', to: '.hex-group[data-ecosystem="tactical"]' },
            { from: '.hex-group[data-ecosystem="satellite"]', to: '.wasp-center-svg' },
            { from: '.hex-group[data-ecosystem="tower"]', to: '.wasp-center-svg' },
            { from: '.hex-group[data-ecosystem="routes"]', to: '.wasp-center-svg' },
            { from: '.hex-group[data-ecosystem="radio"]', to: '.wasp-center-svg' },
            { from: '.hex-group[data-ecosystem="tactical"]', to: '.wasp-center-svg' }
        ];
    }
    
    getElementCenter(selector) {
        const element = document.querySelector(selector);
        if (!element) return null;
        
        const rect = element.getBoundingClientRect();
        const containerRect = this.canvas.parentElement.getBoundingClientRect();
        
        return {
            x: rect.left + rect.width / 2 - containerRect.left,
            y: rect.top + rect.height / 2 - containerRect.top
        };
    }
    
    createParticle(from, to) {
        if (!from || !to) return null;
        
        return {
            x: from.x,
            y: from.y,
            targetX: to.x,
            targetY: to.y,
            progress: 0,
            speed: 0.0005 + Math.random() * 0.001,
            size: 1 + Math.random() * 1.5,
            color: `hsla(${35 + Math.random() * 20}, 45%, 45%, ${0.4 + Math.random() * 0.3})`,
            life: 1.0
        };
    }
    
    updateParticle(particle) {
        // Velocidad variable según el progreso
        let currentSpeed = particle.speed;
        
        if (particle.progress < 0.3) {
            // Polen de entrada: velocidad ligeramente aumentada
            currentSpeed = particle.speed * 1.5;
        } else if (particle.progress > 0.7) {
            // Polen de salida: velocidad muy reducida  
            currentSpeed = particle.speed * 0.3;
        }
        
        particle.progress += currentSpeed;
        
        // Movimiento bezier suave
        const t = particle.progress;
        const controlX = (particle.x + particle.targetX) / 2 + (Math.sin(t * Math.PI) * 50);
        const controlY = (particle.y + particle.targetY) / 2 - 30;
        
        particle.x = this.bezierPoint(particle.x, controlX, particle.targetX, t);
        particle.y = this.bezierPoint(particle.y, controlY, particle.targetY, t);
        
        // Reducir vida gradualmente
        particle.life = Math.max(0, 1 - t);
        
        return particle.progress < 1;
    }
    
    bezierPoint(p0, p1, p2, t) {
        const u = 1 - t;
        return u * u * p0 + 2 * u * t * p1 + t * t * p2;
    }
    
    drawParticle(particle) {
        this.ctx.save();
        this.ctx.globalAlpha = particle.life;
        this.ctx.fillStyle = particle.color;
        this.ctx.beginPath();
        this.ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
        this.ctx.fill();
        
        // Glow effect
        this.ctx.shadowBlur = 8;
        this.ctx.shadowColor = particle.color;
        this.ctx.fill();
        
        this.ctx.restore();
    }
    
    animate() {
        if (!this.isActive) return;
        
        // Limpiar canvas
        this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
        
        // Actualizar y dibujar partículas
        this.particles = this.particles.filter(particle => {
            const alive = this.updateParticle(particle);
            if (alive) {
                this.drawParticle(particle);
            }
            return alive;
        });
        
        // Crear nuevas partículas aleatoriamente
        if (Math.random() < this.particleFrequency) {
            const connection = this.connections[Math.floor(Math.random() * this.connections.length)];
            const from = this.getElementCenter(connection.from);
            const to = this.getElementCenter(connection.to);
            
            const particle = this.createParticle(from, to);
            if (particle) {
                this.particles.push(particle);
            }
        }
        
        this.animationId = requestAnimationFrame(() => this.animate());
    }
    
    start() {
        if (this.isActive) return;
        
        this.isActive = true;
        this.animate();
    }
    
    stop() {
        this.isActive = false;
        if (this.animationId) {
            cancelAnimationFrame(this.animationId);
            this.animationId = null;
        }
        this.particles = [];
        if (this.ctx) {
            this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
        }
    }
    
    bindEvents() {
        // Iniciar cuando la sección sea visible
        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    this.start();
                } else {
                    this.stop();
                }
            });
        }, { threshold: 0.3 });
        
        const waspSection = document.querySelector('.wasp-hex-section');
        if (waspSection) {
            observer.observe(waspSection);
        }
        
        // Redimensionar canvas
        window.addEventListener('resize', () => {
            this.resizeCanvas();
        });
        
        // Pausar/reanudar en hover
        const waspHexContent = document.querySelector('.wasp-hex-content');
        if (waspHexContent) {
            waspHexContent.addEventListener('mouseenter', () => {
                // Aumentar frecuencia de partículas en hover
                this.particleFrequency = 0.25;
            });
            
            waspHexContent.addEventListener('mouseleave', () => {
                this.particleFrequency = 0.15;
            });
        }
    }
}

// Inicialización
document.addEventListener('DOMContentLoaded', function() {
    initializeHoneycombConnections();
});

document.addEventListener('turbo:load', function() {
    initializeHoneycombConnections();
});

function initializeHoneycombConnections() {
    // Esperar a que los elementos estén listos
    setTimeout(() => {
        if (typeof window.honeycombConnections !== 'undefined') {
            window.honeycombConnections.stop();
        }
        
        window.honeycombConnections = new HoneycombConnections();
    }, 2000); // Esperar después de las animaciones iniciales
}